import React, { useMemo, useState } from "react";
import {
  CircularGauge,
  Scale,
  Label,
  RangeContainer,
  Range,
  ValueIndicator,
  Title,
} from "devextreme-react/circular-gauge";
import "./MarketRiskGauge.css";
import { Column, DataGrid } from "devextreme-react/data-grid";

interface UtilYearEntry {
  risk_limit: string;
  mean: number;
  median: number;
  p75: number;
  max: number;
  [key: string]: any;
}

interface MarketRiskGaugeProps {
  gaugeReading: number | null;
  util_years: Record<string, UtilYearEntry>;
}

interface GaugeGridRow {
  id: number;
  label: string;
  [key: string]: any;
}

const MarketRiskGauge: React.FC<MarketRiskGaugeProps> = ({ gaugeReading, util_years }) => {
  const [showTable, setShowTable] = useState(true);

  // Gauge value (mocked)
  const gaugeValue = 110;


function getColorForValue(v: number): string {
  //v = Math.round((v / 180) * 100);
  if (v < 60) return "#00c853";   // green
  if (60<v && v < 80) return "#ffeb3b";   // yellow
  return "#f44336";               // red
}
const asPct = (v: any, dp: number = 2): number | null => {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100 * 10 ** dp) / 10 ** dp; // numeric, e.g. 12.34
};

function convertToGaugeGridData(data: Record<string, UtilYearEntry>): GaugeGridRow[] {
  const years = Object.keys(data)
    .sort((a, b) => Number(b.slice(1)) - Number(a.slice(1))); // ["F26","F25","F24","F23","F22"]
  const outYears = years.map(y => "FY" + y.slice(1));         // ["FY26","FY25",...]

  const rows: GaugeGridRow[] = [];

  const limitRow: GaugeGridRow = { id: 1, label: "Limit" };
  years.forEach((fy, idx) => {
    const outKey = outYears[idx];
    limitRow[outKey] = data[fy].risk_limit; // keep "1.80M" etc as-is
  });
  rows.push(limitRow);

  const defs = [
    //{ id: 2, label: "25%",     field: "p25"    },
    { id: 3, label: "Mean",    field: "mean"    },
    { id: 4, label: "Median", field: "median" },
    { id: 5, label: "75%",    field: "p75"    },
    { id: 6, label: "Max",    field: "max"    }
  ];

  defs.forEach(def => {
    const row: GaugeGridRow = { id: def.id, label: def.label };
    years.forEach((fy, idx) => {
      const outKey = outYears[idx];
      row[outKey] = asPct(data[fy]?.[def.field], 2);
    });
    rows.push(row);
  });

  return rows;
}
const percentCell = (cell: any) => {
  const v = cell.value;
  return Number.isFinite(v) ? `${v.toFixed(2)}%` : "";
};


// const gague_grid_data = convertToGaugeGridData(src);
// console.log(gague_grid_data);

const needleColor = useMemo(() => getColorForValue(gaugeReading ?? 0), [gaugeReading]);

  return (
    <div className="sector-dashboard">
      {/* Gauge */}
      <h3 style={{textAlign: "center"}}>VaR Limit Utilisation</h3>
      <div className="sector-gauge-container">
        <CircularGauge value={gaugeReading ?? 0} size={{ width: 300, height: 300 }}>
  <Scale startValue={0} endValue={100} tickInterval={10}>
    <Label customizeText={(arg: any) => `${Math.round((arg.value / 100) * 100)}%`} />
  </Scale>
  <RangeContainer>
    <Range startValue={0} endValue={60} color="#2ecc71" />
    <Range startValue={60} endValue={80} color="#f1c40f" />
    <Range startValue={80} endValue={100} color="#e74c3c" />
  </RangeContainer>
  {/*<ValueIndicator type="triangleNeedle" color="#2c3e50" />*/}
           <ValueIndicator
        type="triangleNeedle"
        color={needleColor}
        width={10}
        spindleSize={16}
        offset={5}
      />

  {/*<Title text="Gauge Reading (%)" />*/}
</CircularGauge>
      </div>

      {/* Sector Breakdown */}
      <div className="sector-section23">
       <DataGrid
              className="compact-grid"
      dataSource={Object.keys(util_years ?? {}).length ? convertToGaugeGridData(util_years) : []}
      keyExpr="id"
      showBorders={true}
      columnAutoWidth={true}
    >
      <Column dataField="label" caption="" />
      <Column dataField="FY26" caption="FY-26"  cellRender={(cell: any) => {
    if (cell.data?.label === "Limit") return <span>{cell.value ?? ""}</span>;
    const v = cell.value;
    return <span>{Number.isFinite(v) ? `${v.toFixed(0)}%` : ""}</span>;
  }}/>
      <Column dataField="FY25" caption="FY-25"  cellRender={(cell: any) => {
    if (cell.data?.label === "Limit") return <span>{cell.value ?? ""}</span>;
    const v = cell.value;
    return <span>{Number.isFinite(v) ? `${v.toFixed(0)}%` : ""}</span>;
  }}/>
      <Column dataField="FY24" caption="FY-24"  cellRender={(cell: any) => {
    if (cell.data?.label === "Limit") return <span>{cell.value ?? ""}</span>;
    const v = cell.value;
    return <span>{Number.isFinite(v) ? `${v.toFixed(0)}%` : ""}</span>;
  }}/>
      <Column dataField="FY23" caption="FY-23"  cellRender={(cell: any) => {
    if (cell.data?.label === "Limit") return <span>{cell.value ?? ""}</span>;
    const v = cell.value;
    return <span>{Number.isFinite(v) ? `${v.toFixed(0)}%` : ""}</span>;
  }}/>
      <Column dataField="FY22" caption="FY-22"  cellRender={(cell: any) => {
    if (cell.data?.label === "Limit") return <span>{cell.value ?? ""}</span>;
    const v = cell.value;
    return <span>{Number.isFinite(v) ? `${v.toFixed(0)}%` : ""}</span>;
  }}/>
    </DataGrid>

      </div>
    </div>
  );
};

export default MarketRiskGauge;
